import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HexAlphaColorPicker } from "react-colorful";

import { Editor, TLShapeId } from "@tldraw/tldraw";
import "./styles.css";
import { hideSelectionTemporarily, setOpacityPercentage, updateShapeMouseDown, updateShapeMouseUp } from "../../utils";
import { useBrandKitStore, MAX_BRAND_COLORS } from "store/brandKit";
import { useDragToAdjust } from "../hooks/useDragToAdjust";

const presetColors = [
  "#ffb74d",
  "#e57373",
  "#ff6f61",
  "#f06292",
  "#ba68c8",
  "#9575cd",
  "#7986cb",
  "#64b5f6",
  "#4fc3f7",
  "#4dd0e1",
  "#4db6ac",
  "#81c784",
  "#aed581",
  "#dce775",
];

export const handleCopy = (ref: any, setState: any) => {
  if (ref.current) {
    navigator.clipboard
      .writeText(ref.current.value || "")
      .then(() => {
        setState(true);
        setTimeout(() => setState(false), 2000);
      })
      .catch((error) => console.error("Error copying text: ", error));
  }
};

interface ColorPickerProps {
  editor: Editor;
  colorProp?: any;
  shapeColor: string;
  showColorPickerSetMode?: boolean;
  isSelectedPipette?: boolean;
  onColorChange?: (color: string) => void;
  selectedShape?: any;
}

const CustomColorPicker = ({
  editor,
  colorProp,
  shapeColor,
  showColorPickerSetMode,
  isSelectedPipette,
  onColorChange,
  selectedShape,
}: ColorPickerProps) => {
  const hexRef = useRef<HTMLInputElement>(null);
  const [isCopied, setIsCopied] = useState(false);
  const isUserInteractingRef = useRef(false);
  const [colorMode, setColorMode] = useState<"HEX" | "RGB" | "HSL">("HEX");
  const [colorModeOpen, setColorModeOpen] = useState(false);
  const colorModeRef = useRef<HTMLDivElement>(null);

  // Get the actual color for front/back labels
  const getActualColor = () => {
    let color = shapeColor || "#e3e3e3"; // Fallback if shapeColor is undefined
    if (selectedShape?.id === "shape:frontLabel") {
      const frontShape = editor.getShape("shape:front" as TLShapeId);
      color = (frontShape?.props as any)?.fill || shapeColor || "#e3e3e3";
    } else if (selectedShape?.id === "shape:backLabel") {
      const backShape = editor.getShape("shape:back" as TLShapeId);
      color = (backShape?.props as any)?.fill || shapeColor || "#e3e3e3";
    }
    // Normalize to lowercase for consistency
    return (color || "#e3e3e3").toLowerCase();
  };

  const [hexValue, setHexValue] = useState(getActualColor());

  // Load stored colors from localStorage when the component mounts
  const initialPickedColors = useMemo(() => {
    const storedColors = localStorage.getItem("pickedColors");
    return storedColors ? JSON.parse(storedColors) : [];
  }, []);

  const [eyeDropperItems, setEyeDropperItems] = useState<string[]>(initialPickedColors);

  const updateLocalStorage = useCallback((colors: string[]) => {
    localStorage.setItem("pickedColors", JSON.stringify(colors));
  }, []);

  // Brand colors from store
  const { colors: brandColors, addColor: addBrandColor } = useBrandKitStore();

  const handleAddBrandColor = useCallback(() => {
    // Strip alpha channel if present (e.g. "#ff0000ff" -> "#ff0000")
    const hex = hexValue.length > 7 ? hexValue.slice(0, 7) : hexValue;
    addBrandColor({ hexValue: hex });
  }, [hexValue, addBrandColor]);

  // Whether the current color is already saved in the Brand Kit. When false,
  // the "+" button pulses to signal that the color can be saved.
  const isCurrentColorInBrandKit = useMemo(() => {
    const currentHex6 = (hexValue.length > 7 ? hexValue.slice(0, 7) : hexValue).toLowerCase();
    return brandColors.some((bc) => {
      const bcHex = (bc.hexValue || "").toLowerCase();
      const bcHex6 = bcHex.length > 7 ? bcHex.slice(0, 7) : bcHex;
      return bcHex6 === currentHex6;
    });
  }, [hexValue, brandColors]);

  // Close color mode dropdown on outside click
  useEffect(() => {
    if (!colorModeOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (colorModeRef.current && !colorModeRef.current.contains(e.target as Node)) {
        setColorModeOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [colorModeOpen]);

  // Opacity percentage from hex alpha
  const opacityPercent = useMemo(() => {
    if (hexValue.length === 9) {
      const alpha = parseInt(hexValue.slice(7, 9), 16);
      return Math.round((alpha / 255) * 100);
    }
    return 100;
  }, [hexValue]);

  // Color display value based on mode
  const colorDisplayValue = useMemo(() => {
    const hex6 = hexValue.length > 7 ? hexValue.slice(0, 7) : hexValue;
    if (colorMode === "HEX") return hex6.toUpperCase();
    const r = parseInt(hex6.slice(1, 3), 16) || 0;
    const g = parseInt(hex6.slice(3, 5), 16) || 0;
    const b = parseInt(hex6.slice(5, 7), 16) || 0;
    if (colorMode === "RGB") return `${r}, ${g}, ${b}`;
    // HSL
    const rn = r / 255,
      gn = g / 255,
      bn = b / 255;
    const max = Math.max(rn, gn, bn),
      min = Math.min(rn, gn, bn);
    const l = (max + min) / 2;
    if (max === min) return `0°, 0%, ${Math.round(l * 100)}%`;
    const d = max - min;
    const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    let h = 0;
    if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
    else if (max === gn) h = ((bn - rn) / d + 2) / 6;
    else h = ((rn - gn) / d + 4) / 6;
    return `${Math.round(h * 360)}°, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%`;
  }, [hexValue, colorMode]);

  // Update the color of the shape bg-format
  const updateShapeFormat = (shapeId: TLShapeId, fill: string) => {
    const shapeFormat = editor.getShape(shapeId);
    editor.run(
      () => {
        if (shapeFormat) {
          editor.updateShape({
            id: shapeId,
            type: shapeFormat.type,
            props: {
              ...shapeFormat.props,
              fill: fill,
            },
          });
        }
      },
      { ignoreShapeLock: true }
    );
  };

  useEffect(() => {
    if (isSelectedPipette) {
      handleEyeDropper();
    }
  }, [isSelectedPipette]);

  // Update hexValue when the actual color changes
  useEffect(() => {
    const actualColor = getActualColor();

    // Don't update if user is actively interacting with the picker
    if (isUserInteractingRef.current) {
      return;
    }

    // Only update if the color actually changed to avoid overwriting user input
    setHexValue((prevHexValue) => {
      const prevLower = prevHexValue.toLowerCase();
      const actualLower = actualColor.toLowerCase();
      if (actualLower !== prevLower) {
        return actualColor;
      }
      return prevHexValue;
    });
  }, [selectedShape, editor, shapeColor]);

  const updateShapeProps = (shape: any, color: string) => {
    const updatedProps = { ...shape.props };
    if (colorProp && (colorProp.startsWith("outShadowArray.") || colorProp.startsWith("inShadowArray."))) {
      const [_, shadowId, prop] = colorProp.split(".");
      const shadowType = colorProp.startsWith("outShadowArray.") ? "outShadowArray" : "inShadowArray";
      if (shadowId && prop) {
        updatedProps[shadowType] = {
          ...updatedProps[shadowType],
          [shadowId]: {
            ...updatedProps[shadowType][shadowId],
            id: shadowId,
            [prop]: color,
            dx: updatedProps[shadowType][shadowId]?.dx ?? 0,
            dy: updatedProps[shadowType][shadowId]?.dy ?? 0,
            shadowBlur: updatedProps[shadowType][shadowId]?.shadowBlur ?? 0,
            opacity: updatedProps[shadowType][shadowId]?.opacity ?? 1,
            isHidden: updatedProps[shadowType][shadowId]?.isHidden ?? false,
          },
        };
      }
    } else if (colorProp && colorProp.startsWith("colorMix.")) {
      const index = colorProp.split(".")[1];
      updatedProps.colorMix = { ...updatedProps.colorMix };
      updatedProps.colorMix[index] = {
        color,
        isHidden: updatedProps.colorMix[index]?.isHidden ?? false,
      };
    } else if (colorProp && colorProp.startsWith("outlineColorMix.")) {
      const index = colorProp.split(".")[1];
      updatedProps.outlineColorMix = { ...updatedProps.outlineColorMix };
      updatedProps.outlineColorMix[index] = {
        color,
        isHidden: updatedProps.outlineColorMix[index]?.isHidden ?? false,
      };
    } else if (colorProp) {
      updatedProps[colorProp] = color;
    }
    editor.updateShape({
      ...shape,
      props: updatedProps,
    });
  };

  const handleEyeDropper = useCallback(async () => {
    if (!(window as any).EyeDropper) {
      alert("Eye Dropper API is not supported by your browser");
      return;
    }

    try {
      // Mark that user is interacting
      isUserInteractingRef.current = true;

      const eyeDropper = new (window as any).EyeDropper();
      const result = await eyeDropper.open();

      const normalizedColor = result.sRGBHex.toLowerCase();
      setHexValue(normalizedColor);

      // Add the color to the user's recent colors
      setEyeDropperItems((prevColors) => {
        if (!prevColors.includes(normalizedColor)) {
          const updatedColors = [...prevColors, normalizedColor];
          updateLocalStorage(updatedColors);
          return updatedColors;
        }
        return prevColors;
      });

      // showColorPickerSetMode only set colors to Picked colors mode
      if (showColorPickerSetMode) {
        // Clear flag after setting color
        requestAnimationFrame(() => {
          isUserInteractingRef.current = false;
        });
        return;
      }

      const selectedShapeIds = editor.getSelectedShapeIds();

      selectedShapeIds.forEach((shapeId) => {
        const shape = editor.getShape(shapeId);

        if (shape?.id === "shape:frontLabel") {
          updateShapeFormat("shape:front" as TLShapeId, normalizedColor);
          updateShapeFormat("shape:frontBlackLine" as TLShapeId, normalizedColor);
        }

        if (shape?.id === "shape:backLabel") {
          updateShapeFormat("shape:back" as TLShapeId, normalizedColor);
          updateShapeFormat("shape:backBlackLine" as TLShapeId, normalizedColor);
        }

        // Change the color of the shape
        // Don't update group shapes directly - they don't have fill/color props
        if (shape && shape.type !== "group") {
          editor.run(() => updateShapeProps(shape, normalizedColor), { ignoreShapeLock: true });
          // hideSelectionTemporarily(shape, editor);
        }
      });

      // Clear flag after updating shapes
      requestAnimationFrame(() => {
        isUserInteractingRef.current = false;
      });
    } catch (error) {
      console.error("Error:", error);
      // Clear flag on error
      isUserInteractingRef.current = false;
    }
  }, [updateLocalStorage, editor, colorProp, showColorPickerSetMode]);

  const handleColorPickerChange = useCallback(
    (color: string) => {
      // Mark that user is interacting
      isUserInteractingRef.current = true;

      // Normalize color to lowercase for consistency
      const normalizedColor = color.toLowerCase();
      setHexValue(normalizedColor);

      // Notify parent component about the color change
      if (onColorChange) {
        onColorChange(normalizedColor);
      }

      // showColorPickerSetMode only set colors to Picked colors mode
      if (showColorPickerSetMode) return;

      // Only update the specific shape passed as prop, not all selected shapes
      if (selectedShape) {
        if (selectedShape.id === "shape:frontLabel") {
          updateShapeFormat("shape:front" as TLShapeId, normalizedColor);
          updateShapeFormat("shape:frontBlackLine" as TLShapeId, normalizedColor);
        }

        if (selectedShape.id === "shape:backLabel") {
          updateShapeFormat("shape:back" as TLShapeId, normalizedColor);
          updateShapeFormat("shape:backBlackLine" as TLShapeId, normalizedColor);
        }

        // Don't update group shapes directly - ColorPickerPopover handles this via selectedShapes
        // Groups don't have fill/color props, so we skip them here
        if (selectedShape.type !== "group") {
          editor.run(() => updateShapeProps(selectedShape, normalizedColor), { ignoreShapeLock: true });
        }
        // hideSelectionTemporarily(selectedShape, editor);
      }
    },
    [editor, colorProp, showColorPickerSetMode, onColorChange, selectedShape]
  );

  // Handler for button clicks (preset colors, picked colors)
  const handleColorButtonClick = useCallback(
    (color: string) => {
      isUserInteractingRef.current = true;
      handleColorPickerChange(color);
      // Clear flag after next animation frame for button clicks
      requestAnimationFrame(() => {
        isUserInteractingRef.current = false;
      });
    },
    [handleColorPickerChange]
  );

  // Drag-to-adjust on the opacity % display
  const opacityPercentRef = useRef(0);
  opacityPercentRef.current = opacityPercent;
  const opacityDrag = useDragToAdjust(
    () => opacityPercentRef.current,
    (next) => {
      const clamped = Math.max(0, Math.min(100, next));
      const newColor = setOpacityPercentage(hexValue, clamped);
      handleColorPickerChange(newColor);
    },
    { min: 0, max: 100, step: 1 }
  );

  const handleHexInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value;
    setHexValue(input);
    // Apply color and update shapes when valid hex
    if (/^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(input)) {
      handleColorPickerChange(input);
      // Clear interacting flag after shape update completes
      requestAnimationFrame(() => {
        isUserInteractingRef.current = false;
      });
    }
  };

  const handleHexInputBlur = () => {
    isUserInteractingRef.current = false;
    if (!/^#([0-9A-F]{3}|[0-9A-F]{6}|[0-9A-F]{8})$/i.test(hexValue)) {
      setHexValue(getActualColor());
    }
  };

  return (
    <>
      <div
        className="flex flex-col !gap-5 justify-center items-center bg-white transition-all duration-300 border-stone-300"
        // Pairs with `alignContent: center` on the outer popover
        // wrapper so the picker sits vertically centered inside the
        // popover (the wrapper height is now ~10px taller than the
        // picker content, and `align-self` collaborates with the
        // wrapper's `align-content` to center this block).
        style={{ alignSelf: "center" }}
      >
        <div
          className="w-full h-full"
          onMouseDown={() => {
            isUserInteractingRef.current = true;
            if (!selectedShape) return;
            // Only call updateShapeMouseDown if selectedShape is not a group
            if (selectedShape.type !== "group") {
              updateShapeMouseDown(selectedShape, editor);
            }
          }}
          onMouseUp={() => {
            if (!selectedShape) return;
            // Only call updateShapeMouseUp if selectedShape is not a group
            if (selectedShape.type !== "group") {
              updateShapeMouseUp(selectedShape, editor);
            }
            // Clear flag immediately after mouse up
            requestAnimationFrame(() => {
              isUserInteractingRef.current = false;
            });
          }}
        >
          <HexAlphaColorPicker
            color={hexValue}
            onChange={(color) => {
              handleColorPickerChange(color);
            }}
          />
        </div>

        <div className="flex flex-col w-full">
          <div
            className="flex items-center w-full"
            style={{
              height: 24,
              borderRadius: 4,
              border: "1px solid #e5e5e5",
              background: "white",
              overflow: "visible",
            }}
          >
            {/* Eyedropper button — 32px */}
            <button
              type="button"
              onClick={handleEyeDropper}
              className="flex items-center justify-center shrink-0"
              style={{
                width: 32,
                height: "100%",
                background: "transparent",
                border: "none",
                cursor: "pointer",
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <g clipPath="url(#clip0_7418_65357)">
                  <path
                    d="M12.3643 2.66797C11.8096 2.66797 11.2529 2.87305 10.833 3.29297L9.44238 4.69922L8.94238 4.19922L8.23926 4.90234L8.73926 5.40234L3.98926 10.1523C3.46973 10.6719 3.17285 11.0488 2.98926 11.3867C2.80566 11.7246 2.74121 12.0293 2.70801 12.2305C2.6748 12.4316 2.65723 12.541 2.53613 12.7617C2.41504 12.9824 2.16895 13.3008 1.67676 13.793L1.33301 14.1523L1.67676 14.5117L2.67676 15.5117L3.03613 15.8555L3.39551 15.5117C3.87598 15.0312 4.18262 14.7988 4.39551 14.6836C4.6084 14.5684 4.72168 14.5469 4.92676 14.5117C5.13184 14.4766 5.42676 14.4199 5.77051 14.2305C6.11426 14.041 6.50488 13.7305 7.03613 13.1992L11.7861 8.44922L12.2861 8.94922L12.9893 8.24609L12.4893 7.74609L13.8955 6.35547C14.7354 5.51563 14.7354 4.13281 13.8955 3.29297C13.4756 2.87305 12.9189 2.66797 12.3643 2.66797ZM12.3643 3.66797C12.6592 3.66797 12.9463 3.78125 13.1768 4.01172C13.6357 4.4707 13.6357 5.19336 13.1768 5.65234L11.7861 7.04297L10.1455 5.40234L11.5361 4.01172C11.7666 3.78125 12.0693 3.66797 12.3643 3.66797ZM9.44238 6.10547L11.083 7.74609L6.33301 12.4961C5.84082 12.9883 5.50684 13.2344 5.28613 13.3555C5.06543 13.4766 4.95605 13.4941 4.75488 13.5273C4.55371 13.5605 4.26465 13.6094 3.92676 13.793C3.69629 13.918 3.38379 14.2168 3.08301 14.4805L2.70801 14.1055C2.98145 13.7969 3.28223 13.4805 3.41113 13.2461C3.60059 12.9023 3.65723 12.5918 3.69238 12.3867C3.72754 12.1816 3.74902 12.084 3.86426 11.8711C3.97949 11.6582 4.21191 11.3359 4.69238 10.8555L9.44238 6.10547Z"
                    fill="#2E3860"
                  />
                </g>
                <defs>
                  <clipPath id="clip0_7418_65357">
                    <rect width="16" height="16" fill="white" />
                  </clipPath>
                </defs>
              </svg>
            </button>

            {/* 1st separator — at 32px */}
            <div className="shrink-0" style={{ width: 1, height: "100%", background: "#e5e5e5" }} />

            {/* Color mode dropdown — compact */}
            <div
              ref={colorModeRef}
              className="relative shrink-0 flex items-center justify-center"
              style={{ width: 50 }}
            >
              <button
                type="button"
                onClick={() => setColorModeOpen(!colorModeOpen)}
                className="flex items-center justify-between w-full"
                style={{
                  height: 22,
                  background: "transparent",
                  border: "none",
                  padding: "0 6px",
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#0C172C",
                  cursor: "pointer",
                }}
              >
                <span>{colorMode}</span>
                <svg
                  width="8"
                  height="5"
                  viewBox="0 0 10 6"
                  fill="none"
                  style={{ transform: colorModeOpen ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke="#0C172C"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              {colorModeOpen && (
                <div
                  className="absolute left-0 top-full mt-1 w-full z-50"
                  style={{
                    borderRadius: 4,
                    border: "1px solid #e5e5e5",
                    background: "white",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  }}
                >
                  {(["HEX", "RGB", "HSL"] as const).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      onClick={() => {
                        setColorMode(mode);
                        setColorModeOpen(false);
                      }}
                      className="w-full text-left box-border"
                      style={{
                        display: "block",
                        padding: "4px 6px",
                        fontSize: 10,
                        fontWeight: colorMode === mode ? 600 : 400,
                        color: "#0C172C",
                        background: colorMode === mode ? "#E2E2FC" : "transparent",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* 2nd separator */}
            <div className="shrink-0" style={{ width: 1, height: "100%", background: "#e5e5e5" }} />

            {/* Color value — grows to fit hex/rgb/hsl */}
            <div className="shrink-0 flex items-center justify-center" style={{ width: 110 }}>
              <input
                ref={hexRef}
                type="text"
                value={colorDisplayValue}
                onChange={colorMode === "HEX" ? handleHexInputChange : undefined}
                readOnly={colorMode !== "HEX"}
                onBlur={colorMode === "HEX" ? handleHexInputBlur : undefined}
                onClick={colorMode !== "HEX" ? () => handleCopy(hexRef, setIsCopied) : undefined}
                style={{
                  width: "100%",
                  height: 22,
                  background: "transparent",
                  border: "none",
                  padding: 0,
                  fontSize: 10,
                  fontWeight: 500,
                  color: "#0C172C",
                  cursor: colorMode === "HEX" ? "text" : "pointer",
                  outline: "none",
                  textAlign: "center",
                }}
              />
            </div>

            {/* 3rd separator */}
            <div className="shrink-0" style={{ width: 1, height: "100%", background: "#e5e5e5" }} />

            {/* Opacity percentage — remaining space */}
            <div
              className="flex items-center justify-center select-none"
              style={{
                flex: 1,
                height: 22,
                fontSize: 10,
                fontWeight: 500,
                color: "#0C172C",
                cursor: "ew-resize",
              }}
              onMouseDown={opacityDrag.onMouseDown}
              title="Drag to adjust opacity"
            >
              {opacityPercent}%
            </div>
          </div>

          <div className="mt-2 w-full">
            <div className="flex items-center mt-1 gap-2">
              <p className="text-sm">Brand Colors</p>
              {/* Indicate the cap once it's reached. Same brand-grey
                  used elsewhere for de-emphasised meta text so it
                  reads as auxiliary info, not an error. */}
              {brandColors.length >= MAX_BRAND_COLORS && (
                <span style={{ fontSize: 10, color: "#707087", lineHeight: 1 }}>(max reached)</span>
              )}
            </div>
            <div className="flex flex-wrap gap-[6px] mt-1">
              {brandColors.map((bc) => (
                <button
                  type="button"
                  key={bc.id}
                  onClick={() => handleColorButtonClick(bc.hexValue)}
                  style={{
                    backgroundColor: bc.hexValue,
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    border: "1px solid #E1E6EB",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
              ))}
              {/* Hide the "+ add to brand kit" button entirely once
                  the user has saved the max — the section-header
                  PlusIcon in BrandKit still surfaces the tooltip
                  explaining the cap. */}
              {brandColors.length < MAX_BRAND_COLORS && (
                <button
                  type="button"
                  onClick={handleAddBrandColor}
                  className={!isCurrentColorInBrandKit ? "brand-color-add-pulse" : undefined}
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: 4,
                    border: "1px solid #1A2547",
                    background: "white",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: 0,
                    fontSize: 14,
                    fontWeight: 600,
                    color: "#1A2547",
                    lineHeight: 1,
                  }}
                >
                  +
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomColorPicker;

// Custom CSS for color picker borders
const colorPickerBorderStyle = `
  .color-picker-border {
    border: 1px solid #E1E6EB !important;
    box-sizing: border-box !important;
  }

  /*
   * Pulse/glow applied to the "+" button that adds the current color to the
   * Brand Kit. Runs only when the current color isn't already saved, to hint
   * the user they can save it. Uses box-shadow instead of transform so the
   * button doesn't shift neighboring swatches.
   */
  @keyframes brand-color-add-pulse {
    0%   { box-shadow: 0 0 0 0 rgba(238, 67, 96, 0.55); border-color: #1A2547; }
    50%  { box-shadow: 0 0 0 4px rgba(238, 67, 96, 0); border-color: #EE4360; }
    100% { box-shadow: 0 0 0 0 rgba(238, 67, 96, 0); border-color: #1A2547; }
  }
  .brand-color-add-pulse {
    animation: brand-color-add-pulse 1.6s ease-in-out infinite;
  }
  .brand-color-add-pulse:hover {
    animation: none;
    border-color: #EE4360 !important;
    box-shadow: 0 0 0 2px rgba(238, 67, 96, 0.25);
  }

`;

// Inject the style into the document head
if (typeof document !== "undefined") {
  const styleId = "color-picker-border-style";
  if (!document.getElementById(styleId)) {
    const style = document.createElement("style");
    style.id = styleId;
    style.textContent = colorPickerBorderStyle;
    document.head.appendChild(style);
  }
}
